import { CanMatchFn, Routes, UrlMatchResult, UrlSegment } from '@angular/router';

import { LoggedInGuard } from '~/core/guards/logged-in.guard';
import { UserProfile } from '~/core/constants/user-profile';
import { AuthorizationGuard } from '~/core/guards/authorization.guard';
import { RegisterEnabledGuard } from '~/core/guards/register-enabled-guard';
import { RegisterMobileGuard } from '~/core/guards/register-mobile.guard';
import { CasinoLiveGuard } from '~/core/guards/casino-live.guard';
import { CasinoGuard } from '~/core/guards/casino.guard';
import { GamificationGuard } from '~/core/guards/gamification.guard';
import { RouteRedirectGuard } from '~/core/guards/route-redirect.guard';
import { SportsbookGuard } from '~/core/guards/sportsbook.guard';
import { MainHomeGuard } from '~/core/guards/main-home.guard';
import { signInGuard } from '~/core/guards/sign-in.guard';
import { SportsbookProvider } from '~/core/enum/providers.enum';
import { CompanyService } from '~/core/services/company.service';
import { NgxSportsOnlyGuard } from '~/core/guards/ngx-sports-only.guard';
import { isDesktop } from '~/core/helpers/device-utils';
import { TermsTypes } from '~/containers/dialogs/footer-terms-dialog/terms-type.enum';
import { StateMenu } from '~/core/constants/sport-display';
import { openRegisterGuard } from '~/core/guards/open-register.guard';
import { routedDialogGuard } from '~/core/guards/routed-dialog.guard';
import { WindowPlus2Component } from './window.component';

const loadEventsListDesktop = () => import('~/containers/event-list/event-list.component').then((m) => m.EventListComponent);

const loadEventsAreaDesktop = () =>
  import('~/pages/events-area-plus2/events-area-desktop-plus2/events-area-desktop-plus2.component').then(
    (m) => m.EventsAreaDesktopPlus2Component,
  );

const loadEventsAreaMobile = () =>
  import('~/pages/events-area-plus2/events-area-mobile-plus2/events-area-mobile-plus2.component').then(
    (m) => m.EventsAreaMobilePlus2Component,
  );

const loadTermsAndPolicy = () =>
  import('~/containers/dialogs/footer-terms-dialog/terms-and-policy.component').then((m) => m.TermsAndPolicyComponent);

const loadEventDetails = () =>
  import('~/pages/event-details-plus2/event-details-plus2.component').then((m) => m.EventDetailsPlus2Component);

const isNgxSportsbookProvider: CanMatchFn = () => {
  const provider = CompanyService.currentScreenView?.sportsbook_screen?.provider ?? SportsbookProvider.NGX;
  return provider === SportsbookProvider.NGX;
};

const isNotNgxSportsbookProvider: CanMatchFn = () => {
  const provider = CompanyService.currentScreenView?.sportsbook_screen?.provider ?? SportsbookProvider.NGX;
  return provider !== SportsbookProvider.NGX;
};

const loadAltenarSportsbook = () =>
  import('~/pages/altenar-sportsbook/altenar-sportsbook.component').then((m) => m.AltenarSportsbookComponent);

const loadBetbySportsbook = () => import('~/pages/betby-sportsbook/betby-sportsbook.component').then((m) => m.BetbySportsbookComponent);

const loadFirstSportsbook = () => import('~/pages/first-sportsbook/first-sportsbook.component').then((m) => m.FirstSportsbookComponent);
const loadDigitainSportsbook = () =>
  import('~/pages/digitain-sportsbook/digitain-sportsbook.component').then((m) => m.DigitainSportsbookComponent);

const nonNgxSportsMatcher = (url: UrlSegment[]): UrlMatchResult | null => {
  if (url.length > 0 && (url[0].path === 'sports' || url[0].path === 's')) {
    return { consumed: url };
  }
  return null;
};

const nonNgxLiveMatcher = (url: UrlSegment[]): UrlMatchResult | null => {
  if (url.length > 0 && url[0].path === 'live') {
    return { consumed: url };
  }
  return null;
};

// NOTE: WindowPlus2Component is now imported statically to avoid the "double async hop"
// const windowComponent = () => import('~/pages/window/window.component').then((module) => module.WindowPlus2Component);

export function casinoMatcher(url): any {
  const segment = url[0]?.path;
  const casinoPath = CompanyService.getLotteryCasinoNameSpace?.url_name;
  return segment && segment === casinoPath ? { consumed: [url[0]] } : null;
}

export function casinoLiveMatcher(url): any {
  const segment = url[0]?.path;
  const casinoLivePath = CompanyService.getLotteryCasinoNameSpace?.live_url;
  return segment && segment === casinoLivePath ? { consumed: [url[0]] } : null;
}

const getMainPageComponent = async () => {
  // TODO(MIGRATION-WINDOW-MODULE-MAIN-START): CompanyService.mainStart stays until migrated
  const rawStart = CompanyService.mainStart?.toString().toUpperCase() || '';
  const startMain = rawStart.replace(/-/g, '_');

  switch (startMain) {
    case StateMenu.SPORT:
    case StateMenu.SPORTS:
    case StateMenu.PRE_MATCH:
      return isDesktop() ? loadEventsAreaDesktop() : loadEventsAreaMobile();

    case StateMenu.HOME:
      return (await import('~/pages/home/home.component')).HomeComponent;

    case StateMenu.LIVE:
      return isDesktop() ? loadEventsListDesktop() : loadEventsAreaMobile();

    case StateMenu.CASINO:
      return (await import('~/pages/casino/casino-v2/casino-v2.component')).CasinoV2Component;

    case StateMenu.LIVE_CASINO:
      return (await import('~/pages/casino/casino-v2/casino-v2.component')).CasinoV2Component;

    case StateMenu.VIRTUAL_SPORTS:
      return (await import('~/pages/virtual-sports/virtual-sports.component')).VirtualSportsComponent;

    case StateMenu.E_SPORTS:
      return (await import('~/pages/e-sports/e-sports.component')).ESportsComponent;

    default:
      return (await import('~/pages/home/home.component')).HomeComponent;
  }
};

const routes: Routes = [
  {
    path: '',
    component: WindowPlus2Component,
    data: { root: true },
    canActivate: [openRegisterGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: getMainPageComponent,
        data: { isLive: true, isMain: true, isHome: true, preload: true, shouldReuse: true },
      },
      {
        path: 'home',
        canActivate: [MainHomeGuard],
        loadComponent: () => import('~/pages/home/home.component').then((module) => module.HomeComponent),
        data: { isLive: true, isMain: true, isHome: true, preload: true, shouldReuse: true },
      },
      {
        path: 'pages/:slug',
        loadComponent: () => import('~/pages/pages/pages.component').then((m) => m.PagesComponent),
      },
      {
        path: 'sports',
        canActivate: [SportsbookGuard],
        canMatch: [isNgxSportsbookProvider],
        loadComponent: () => (isDesktop() ? loadEventsAreaDesktop() : loadEventsAreaMobile()),
        data: { isLive: true, isMain: true, isHome: true, preload: true, shouldReuse: true },
      },
      {
        matcher: nonNgxSportsMatcher,
        canActivate: [SportsbookGuard],
        canMatch: [isNotNgxSportsbookProvider],
        loadComponent: () => {
          switch (CompanyService.currentScreenView?.sportsbook_screen?.provider) {
            case SportsbookProvider.ALTENAR:
              return loadAltenarSportsbook();
            case SportsbookProvider.BETBY:
              return loadBetbySportsbook();
            case SportsbookProvider.FIRST:
              return loadFirstSportsbook();
            case SportsbookProvider.DIGITAIN:
              return loadDigitainSportsbook();
            default:
              return isDesktop() ? loadEventsAreaDesktop() : loadEventsAreaMobile();
          }
        },
        data: { shouldReuse: false },
      },
      {
        matcher: nonNgxLiveMatcher,
        canActivate: [SportsbookGuard],
        canMatch: [isNotNgxSportsbookProvider],
        loadComponent: () => {
          switch (CompanyService.currentScreenView?.sportsbook_screen?.provider) {
            case SportsbookProvider.ALTENAR:
              return loadAltenarSportsbook();
            case SportsbookProvider.BETBY:
              return loadBetbySportsbook();
            case SportsbookProvider.FIRST:
              return loadFirstSportsbook();
            case SportsbookProvider.DIGITAIN:
              return loadDigitainSportsbook();
            default:
              return isDesktop() ? loadEventsAreaDesktop() : loadEventsAreaMobile();
          }
        },
        data: { isLiveSports: true, shouldReuse: false },
      },
      {
        matcher: casinoMatcher,
        canActivate: [CasinoGuard],
        loadChildren: () => import(/* webpackChunkName: "casino" */ '../casino/casino.module').then((module) => module.CasinoModule),
        data: { preload: true, shouldReuse: true },
      },
      {
        matcher: casinoLiveMatcher,
        canActivate: [CasinoLiveGuard],
        loadChildren: () =>
          import(/* webpackChunkName: "casino-live" */ '../casino-live/casino-live.module').then((module) => module.CasinoLiveModule),
        data: { preload: true, shouldReuse: true },
      },
      {
        path: 'virtual-sports',
        loadComponent: () =>
          import(/* webpackChunkName: "virtual-sports" */ '../virtual-sports/virtual-sports.component').then(
            (module) => module.VirtualSportsComponent,
          ),
      },
      {
        path: 'sportbook-wireframe',
        loadComponent: () =>
          import(/* webpackChunkName: "sportbook-wireframe" */ '../sportbook-wireframe/sportbook-wireframe.component').then(
            (module) => module.SportbookWireframeComponent,
          ),
      },
      {
        path: 'e-sports',
        loadComponent: () =>
          import(/* webpackChunkName: "e-sports" */ '../e-sports/e-sports.component').then((module) => module.ESportsComponent),
      },
      {
        path: 'promotions',
        loadComponent: () =>
          import(/* webpackChunkName: "promotions" */ '../promotions/promotions.component').then((module) => module.PromotionsComponent),
      },
      {
        path: 'support',
        loadComponent: () =>
          import(/* webpackChunkName: "support" */ '../support/support.component').then((module) => module.SupportComponent),
      },
      {
        path: 'privacy-policy',
        data: { type: TermsTypes.POLICY },
        loadComponent: loadTermsAndPolicy,
        pathMatch: 'full',
      },
      {
        path: 'terms-conditions',
        data: { type: TermsTypes.TERMS_CONDITIONS },
        loadComponent: loadTermsAndPolicy,
        pathMatch: 'full',
      },
      {
        path: 'laundering',
        data: { type: TermsTypes.LAUNDERING },
        loadComponent: loadTermsAndPolicy,
        pathMatch: 'full',
      },
      {
        path: 'game',
        data: { type: TermsTypes.GAME },
        loadComponent: loadTermsAndPolicy,
        pathMatch: 'full',
      },
      {
        path: 'rules',
        loadComponent: () =>
          import(
            /* webpackChunkName: "rules-dialog-mobile" */ '~/containers/dialogs/rules-dialog/rules-dialog-mobile/rules-dialog-mobile.component'
          ).then((m) => m.RulesDialogMobileComponent),
        pathMatch: 'full',
        data: { isLive: false, isMain: true },
      },
      {
        path: 'statistics',
        loadComponent: () =>
          import(/* webpackChunkName: "statistics-center" */ '../statistics-center/statistics-center.component').then(
            (module) => module.StatisticsCenterComponent,
          ),
      },
      {
        path: 'results',
        loadComponent: () =>
          import(/* webpackChunkName: "statistics-center" */ '../statistics-center/statistics-center.component').then(
            (module) => module.StatisticsCenterComponent,
          ),
      },
      {
        path: 's/favorites',
        canActivate: [LoggedInGuard],
        loadComponent: () =>
          import('~/pages/sportsbook-favorites-page/sportsbook-favorites-page.component').then((m) => m.SportsbookFavoritesPageComponent),
        data: { isHome: true },
      },
      {
        path: 'live',
        loadComponent: () => (isDesktop() ? loadEventsAreaDesktop() : loadEventsAreaMobile()),
        data: { isLiveSports: true, isHome: true },
        canMatch: [isNgxSportsbookProvider],
      },
      {
        path: 's/:sportCode',
        loadComponent: loadEventsListDesktop,
        data: { isLive: false, isHome: true },
        canMatch: [NgxSportsOnlyGuard],
      },
      {
        path: 's/:sportCode/e/:eventId',
        loadComponent: loadEventDetails,
        data: { isLive: false, isHome: true, isEventDetails: true },
        canMatch: [NgxSportsOnlyGuard],
      },
      {
        path: 'live/s/:sportCode/e/:eventId',
        loadComponent: loadEventDetails,
        data: { isLive: true, isHome: true, isEventDetails: true },
        canMatch: [NgxSportsOnlyGuard],
      },
      {
        path: 'live/s/:sportCode',
        loadComponent: loadEventsListDesktop,
        data: { isLive: true, isHome: true },
        canMatch: [NgxSportsOnlyGuard],
      },
      {
        path: 'user-dashboard/deposit',
        redirectTo: '/deposit',
        pathMatch: 'full',
      },
      {
        path: 'user-dashboard/withdraw',
        redirectTo: '/withdraw',
        pathMatch: 'full',
      },
      {
        path: 'user-dashboard',
        loadChildren: () =>
          import(/* webpackChunkName: "user-dashboard" */ '~/pages/user-dashboard/user-dashboard.routes').then(
            (module) => module.DASHBOARD_ROUTES,
          ),
        canActivate: [LoggedInGuard, AuthorizationGuard],
        data: { roles: [UserProfile.CLIENT, UserProfile.AGENT, UserProfile.MANAGER] },
      },
      {
        path: 'gamification',
        loadChildren: () =>
          import(/* webpackChunkName: "gamification" */ '~/pages/gamification/gamification.module').then((module) => module.routes),
        canActivate: [LoggedInGuard, GamificationGuard],
      },
      {
        path: 'welcome-new-user',
        redirectTo: '/welcome-new-user',
        pathMatch: 'full',
      },
      {
        path: 'sportsbook',
        loadChildren: () =>
          import(/* webpackChunkName: "sportsbook" */ '../sportsbook/sportsbook.component').then((module) => module.routes),
      },
      {
        path: 'sportsbook-integration',
        loadChildren: () =>
          import(/* webpackChunkName: "sportsbook-integration" */ '../sportsbook-integration/sportsbook-integration.component').then(
            (module) => module.routes,
          ),
      },
    ],
  },
  {
    path: 'signin',
    loadComponent: () =>
      import(/* webpackChunkName: "login-page" */ '../login-page/login-page.component').then((module) => module.LoginPageComponent),
    canActivate: [signInGuard],
    data: { preload: true, shouldReuse: true, overflowPage: true },
  },
  {
    path: 'register',
    loadComponent: () =>
      import(/* webpackChunkName: "register-page" */ '../register-page/register-page.component').then(
        (module) => module.RegisterPageComponent,
      ),
    canActivate: [RouteRedirectGuard, RegisterMobileGuard],
    data: { preload: true, shouldReuse: false, overflowPage: true },
  },
  {
    path: 'deposit',
    loadComponent: () =>
      import(/* webpackChunkName: "routed-dialog-stub" */ '../routed-dialog-stub/routed-dialog-stub.component').then(
        (m) => m.RoutedDialogStubComponent,
      ),
    canActivate: [routedDialogGuard('deposit')],
    data: { overflowPage: true },
  },
  {
    path: 'withdraw',
    loadComponent: () =>
      import(/* webpackChunkName: "routed-dialog-stub" */ '../routed-dialog-stub/routed-dialog-stub.component').then(
        (m) => m.RoutedDialogStubComponent,
      ),
    canActivate: [routedDialogGuard('withdraw')],
    data: { overflowPage: true },
  },
  {
    path: 'register-user',
    canActivate: [RouteRedirectGuard, RegisterEnabledGuard],
    loadComponent: () =>
      import(/* webpackChunkName: "register-user" */ '../register-user-global/register-user-global.component').then(
        (module) => module.RegisterUserGlobalComponent,
      ),
    data: { preload: true, overflowPage: true },
  },
  {
    path: '**',
    loadComponent: () =>
      import(/* webpackChunkName: "redirect" */ '~/components/redirect/redirect.component').then((m) => m.RedirectComponent),
  },
];

export { routes as WindowPLus2Routes };
